import type { TFunction } from "i18next";
import { createElement } from "react";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { U2000StringArrayDisplay } from "../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import { getNestedValue } from "../../../u2000Core/tools/U2000ObjectUtils";
import {
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
  U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE,
  U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES,
} from "../../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  U2000_CRITERIA_INIT_OPTIONS_U3139_FAMILIES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_LEGACY_WORK_NATURE_CODES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES,
  U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES_ID,
} from "../../../u2000Core/components/tables/commons/search/U2000CriteriaInitOptionsRegistry";
import {
  u3139FormatFamilyLabel,
  u3139FormatLegacyWorkNatureDisplay,
  u3139SecondaryWorkNatureListLabels,
  u3139ResolveWorkNatureLabel,
  type U3139EmployeeItem,
  type U3139FamilyItem,
  type U3139WorkNatureItem,
} from "../../U3139Service";

const NS = "u3139";

/** Champs critère retirés du sélecteur UI (alias backend conservés côté API). BL-0109 */
export const U3139_REMOVED_DUPLICATE_CRITERIA_FIELDS = [
  "workNature.id",
  "project.id",
  "surveyor.id",
] as const;

/** Colonne grille arpenteur : nom complet uniquement (le code reste sur le critère `surveyor.codeEmployee`). */
function formatEmployeeCell(value: unknown): string {
  const employee = value as U3139EmployeeItem | null | undefined;
  if (!employee || typeof employee !== "object") return "";
  const firstName = employee.firstName?.trim() ?? "";
  const lastName = employee.lastName?.trim() ?? "";
  const name = `${firstName} ${lastName}`.trim();
  return name || employee.codeEmployee?.trim() || "";
}

function displayEmployeeNestedColumn(column: U2000TableColumn, _value: unknown, row: Record<string, unknown>): string | null {
  const employee = getNestedValue(row, column.name) as U3139EmployeeItem | null | undefined;
  const label = formatEmployeeCell(employee);
  return label || null;
}

export function getU3139MinutesListColumnsDef(
  t: TFunction,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
  _families?: U3139FamilyItem[],
): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "minuteNumber",
      label: t("lblMinuteNumber", { ns: NS }),
      widthClass: "w-[120px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: "clientName",
      label: t("lblClientName", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: "minuteDate",
      label: t("lblMinuteDate", { ns: NS }),
      widthClass: "w-[120px]",
      type: "date",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: "DESC",
    },
    {
      name: "workNature.workNatureCode",
      label: t("lblWorkNature", { ns: NS }),
      widthClass: "w-[180px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "in"],
      criteriaInitOptions: {
        optionsKey: U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES,
        valueKey: "workNatureCode",
        filterActive: true,
      },
      display: (_column, _value, row) => u3139ResolveWorkNatureLabel(row.workNature, languageCode, workNatures),
    },
    {
      name: "workNature.family.id",
      label: t("lblWorkNatureFamily", { ns: NS }),
      widthClass: "w-[160px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      includeConditions: ["equals", "notequals", "in"],
      criteriaInitOptions: {
        optionsKey: U2000_CRITERIA_INIT_OPTIONS_U3139_FAMILIES,
        valueKey: "id",
      },
      display: (_column, _value, row) => u3139FormatFamilyLabel(row.workNature?.family, languageCode),
    },
    {
      name: "secondaryWorkNatures",
      label: t("lblSecondaryWorkNatures", { ns: NS }),
      widthClass: "w-[240px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: false,
      sortable: false,
      noTruncate: true,
      display: (column, _value, row) =>
        createElement(U2000StringArrayDisplay, {
          column,
          row,
          value: u3139SecondaryWorkNatureListLabels(row.secondaryWorkNatures, languageCode, workNatures),
        }),
    },
    {
      name: "secondaryWorkNatures.workNature.id",
      label: t("lblSecondaryWorkNatureCriteria", { ns: NS }),
      widthClass: "w-[180px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      includeConditions: ["equals", "in"],
      criteriaInitOptions: {
        optionsKey: U2000_CRITERIA_INIT_OPTIONS_U3139_WORK_NATURES_ID,
        valueKey: "id",
      },
    },
    {
      name: "legacyWorkNatureCode",
      label: t("lblLegacyWorkNatureCode", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaInitOptions: {
        optionsKey: U2000_CRITERIA_INIT_OPTIONS_U3139_LEGACY_WORK_NATURE_CODES,
        valueKey: "legacyCode",
      },
      display: (_column, _value, row) =>
        u3139FormatLegacyWorkNatureDisplay(row.legacyWorkNatureLabel, row.legacyWorkNatureCode),
    },
    {
      name: "surveyor",
      label: t("lblSurveyor", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      noTruncate: true,
      display: displayEmployeeNestedColumn,
    },
    {
      name: "surveyor.codeEmployee",
      label: t("lblSurveyor", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE,
        searchCriterionField: "codeEmployee",
        valueKey: "codeEmployee",
        criteriaField: "surveyor.codeEmployee",
        displayKeys: ["codeEmployee", "firstName", "lastName"],
        placeholder: t("phCriteriaEmployee", { ns: "common" }),
      },
    },
    {
      name: "streetAddress",
      label: t("lblStreetAddress", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: "planNumber",
      label: t("lblPlanNumber", { ns: NS }),
      widthClass: "w-[120px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
    },
    {
      name: "project.projetNumber",
      label: t("lblProject", { ns: NS }),
      widthClass: "w-[120px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
        searchCriterionField: "projetNumber",
        valueKey: "projetNumber",
        criteriaField: "project.projetNumber",
        displayKeys: ["projetNumber", "projetName"],
        selectedDisplayKeys: ["projetNumber", "projetName"],
        placeholder: t("phCriteriaProject", { ns: "common" }),
      },
    },
    {
      name: "detailLots.initialLot",
      label: t("lblInitialLot", { ns: NS }),
      widthClass: "w-[140px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      includeConditions: ["equals", "notequals", "contains", "startswith", "endswith", "in", "isNull", "isNotNull"],
    },
    {
      name: "detailLots.cadastre",
      label: t("lblCadastre", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES,
        searchCriterionField: "cadastre",
        valueKey: "id",
        criteriaField: "detailLots.cadastre.id",
        displayKeys: ["cadastre"],
        selectedDisplayKeys: ["cadastre"],
        placeholder: t("phCriteriaCadastre", { ns: "common" }),
      },
    },
    {
      name: "isRenovation",
      label: t("lblIsRenovation", { ns: NS }),
      widthClass: "w-[100px]",
      type: "boolean",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      display: (_column, value) =>
        value === true || value === "true" ? t("lblYes", { ns: NS }) : value === false || value === "false" ? t("lblNo", { ns: NS }) : "",
    },
    {
      name: "isInactive",
      label: t("lblIsInactive", { ns: NS }),
      widthClass: "w-[90px]",
      type: "boolean",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      display: (_column, value) =>
        value === true || value === "true" ? t("lblYes", { ns: NS }) : value === false || value === "false" ? t("lblNo", { ns: NS }) : "",
    },
  ];
}

/** Noms des colonnes critère exposées dans le sélecteur U3139. */
export function getU3139CriteriaFieldNames(columns: U2000TableColumn[]): string[] {
  return columns.filter((col) => col.isCriteriaField).map((col) => col.name);
}
